/**
 * Copy and content for the redesigned home page.
 *
 * Source: Claude Design handoff, "Haizea Analytics Website v2". Where noted,
 * content is carried over from v1 of the same handoff because v2 kept it in its
 * data model but dropped it from the rendered layout.
 */

export const SECTION_IDS = {
  mission: "mission",
  problem: "problem",
  approach: "approach",
  howItWorks: "how-it-works",
  capabilities: "capabilities",
  sectors: "sectors",
  terrakio: "terrakio",
  work: "our-work",
  results: "results",
  rigour: "scientific-rigour",
  about: "about",
  process: "how-we-work",
  contact: "contact",
} as const;

export const HERO_TELEMETRY = [
  "35.2809° S · 149.1300° E",
  "SENTINEL-2 · MODIS · LIDAR",
];

/** Carried over from v1 — the credibility strip v2 dropped from the hero. */
export const HERO_STATS = [
  { value: "150+", label: "Datasets in the archive" },
  { value: "7.7M km²", label: "Continental coverage" },
  { value: "Decades", label: "Of observation, current to today" },
];

export const PROBLEMS = [
  {
    num: "01",
    title: "Fragmented sources",
    body: "Hundreds of missions and sensors, each with its own format, resolution and gaps to reconcile.",
  },
  {
    num: "02",
    title: "Heavy preparation",
    body: "Cleaning, aligning and validating before a single question can be asked of the data.",
  },
  {
    num: "03",
    title: "Serious investment",
    body: "Standing capability for storage, compute and specialists — before any of your data produces an answer.",
  },
  {
    num: "04",
    title: "Start again each time",
    body: "Each new question repeats much of the work, so the effort invested never becomes a lasting capability.",
  },
];

export const APPROACH = [
  {
    num: "01",
    title: "Ask in your own terms",
    body: "Describe the place and the question: a property, a catchment, a season, a decade. Not the datasets, projections or processing steps behind it.",
  },
  {
    num: "02",
    title: "Delivered the way you work",
    body: "A report for your executive, a live map for your team, or a feed into a system you already use. The answer arrives where the decision gets made.",
  },
  {
    num: "03",
    title: "Answers that stay current",
    body: "Not a map with a date on it. As new observations arrive the answer updates, so what you relied on last quarter still holds this one.",
  },
  {
    num: "04",
    title: "Nothing to build",
    body: "No cluster to provision, no specialists to recruit, no infrastructure to maintain. You pay for the answers, not the capacity to produce them.",
  },
];

export const PIPELINE = [
  {
    num: "01",
    title: "Data",
    body: "Satellite, climate, elevation — and your own layers.",
    img: "/images/croptypes1.webp",
    alt: "Analysis-ready Sentinel-2 imagery",
  },
  {
    num: "02",
    title: "Terrak.io",
    body: "Hundreds of sources, one validated archive.",
    img: "/images/our-data.webp",
    alt: "Stacked analysis-ready data layers",
  },
  {
    num: "03",
    title: "Science + AI",
    body: "Peer-reviewed methods, built to run at scale.",
    img: "/images/globseas1.webp",
    alt: "Global MODIS composite",
  },
  {
    num: "04",
    title: "Analytics",
    body: "Decades of record, queried in seconds.",
    img: "/images/croptypes2.webp",
    alt: "Derived crop cycle classification",
  },
  {
    num: "05",
    title: "Answer",
    body: "A report, a live map, or a feed you already use.",
    img: "/images/darling2.webp",
    alt: "Flood duration derived from Sentinel-2",
  },
  {
    num: "06",
    title: "Decision",
    body: "Delivered where the decision gets made.",
    img: "/images/africa2.webp",
    alt: "Vegetation anomaly analysis",
  },
];

export const PIPELINE_TAGS = [
  "Irrigation compliance",
  "Forest change",
  "Biodiversity assessment",
  "Hazard response",
];

/**
 * Four capability groups. v2 rendered three cards but kept all four in its data
 * model; v1 rendered the fourth ("Reporting and compliance"), so it is restored
 * here with the fuller v1 bullet lists.
 */
export const CAPABILITIES = [
  {
    kicker: "C1 · Custom build",
    kickerColor: "var(--color-mint)",
    img: "/images/croptypes2.webp",
    alt: "Field-level classification across a cropping landscape",
    title: "Custom geospatial solutions",
    body: "Your data, explored interactively — or our technology powering your branded product.",
    items: [
      "Tailored web applications",
      "White-label deployments",
      "Integration with existing systems",
      "Scalable architecture",
    ],
  },
  {
    kicker: "C2 · Data",
    kickerColor: "var(--color-cyan)",
    img: "/images/our-data.webp",
    alt: "Stacked analysis-ready data layers",
    title: "Data services",
    body: "Streams blended across missions and models, cleaned and prepared for analysts, models and dashboards.",
    items: [
      "AI-driven data fusion",
      "Analysis-ready imagery",
      "150+ datasets, decades deep",
      "One common interface",
    ],
  },
  {
    kicker: "C3 · Intelligence",
    kickerColor: "var(--color-ember)",
    img: "/images/africa2.webp",
    alt: "Vegetation anomalies indicating drought impact",
    title: "AI environmental intelligence",
    body: "Insight into how landscapes behave, and early warning when they don't behave as expected.",
    items: [
      "Predictive analytics on archival data",
      "Anomaly detection at pixel to catchment scale",
      "Vegetation and change analysis",
      "Risk intelligence",
    ],
  },
  {
    kicker: "C4 · Evidence",
    kickerColor: "var(--color-paper-2)",
    img: "/images/nsw-water-compliance.webp",
    alt: "Water licence compliance monitoring",
    title: "Reporting and compliance",
    body: "Monitoring that runs on its own and evidence that stands up when it is questioned.",
    items: [
      "Automated reporting",
      "Continuous compliance monitoring",
      "Historical trend analysis",
      "Defensible, traceable evidence",
    ],
  },
];

export const SECTORS = [
  {
    key: "Resources",
    kicker: "Resources",
    source: "Sentinel-2 · Darling River",
    title: "Water authorities, land and resource agencies, agribusiness.",
    body: "Monitoring use, compliance and productivity across areas too large to inspect on the ground.",
    points: [
      "Licence and allocation compliance screening",
      "Irrigated area and crop cycle mapping",
      "Flood duration and water extent over time",
    ],
    img: "/images/darling2.webp",
    alt: "Flood extent along the Darling River from Sentinel-2",
  },
  {
    key: "Environment",
    kicker: "Environment",
    source: "Continental monitoring",
    title: "Environmental agencies, NGOs and conservation organisations.",
    body: "Tracking vegetation, forests, biodiversity and land condition over time.",
    points: [
      "Vegetation and forest change, annually updated",
      "Continental-scale condition reporting",
      "Biodiversity and carbon potential at property scale",
    ],
    img: "/images/aus-env-report-2.webp",
    alt: "Continental vegetation and land condition",
  },
  {
    key: "Hazards",
    kicker: "Hazards",
    source: "Thermal · imagery · lightning",
    title: "Emergency and hazard agencies.",
    body: "Continuous monitoring of fire, flood and other hazards, turning scattered detections into early situational awareness.",
    points: [
      "Scattered detections fused into named incidents",
      "Continental coverage, always on",
      "Feeds into the systems agencies already run",
    ],
    img: "/images/ozhazard-bushfire.webp",
    alt: "Bushfire front from the air",
  },
  {
    key: "Land & property",
    kicker: "Land and property",
    source: "Property-scale analysis",
    title: "Buyers, sellers, underwriters and insurers of rural land.",
    body: "Alongside farmers, land managers and project developers — detailed information at the level of a single property.",
    points: [
      "Property-scale history, decades deep",
      "Spatial evidence for valuation and due diligence",
      "Carbon and biodiversity assessment on demand",
    ],
    img: "/images/aerial-view-of-australian-suburb.webp",
    alt: "Aerial view of Australian land parcels",
  },
];

export const ARCHIVE_FRAMES = [
  {
    stage: "Earliest record",
    img: "/images/darling1.webp",
    alt: "Analysis-ready Sentinel-2 imagery of a river system",
    caption:
      "Decades of observation, aligned and validated the moment it entered the archive.",
  },
  {
    stage: "Mid-record",
    img: "/images/croptypes1.webp",
    alt: "Analysis-ready imagery of a cropping landscape",
    caption:
      "Every mission reconciled against the next, so the record is continuous in space and time.",
  },
  {
    stage: "Latest observation",
    img: "/images/croptypes2.webp",
    alt: "Derived crop cycle classification",
    caption:
      "Current to the latest pass — the answer updates rather than expiring.",
  },
];

export const TERRAK_STATS = [
  { value: "150+", label: "Datasets, one interface" },
  { value: "Decades", label: "Of aligned history" },
  { value: "Seconds", label: "To a full-detail answer" },
];

export const TERRAK_STEPS = [
  { label: "Archive", value: "Sentinel-2 · aligned, validated" },
  { label: "Method", value: "LiDAR-trained model · 10 m resolution" },
  { label: "Compute", value: "Full archive, full detail" },
  { label: "Output", value: "GeoTIFF + JSON + map tiles" },
];

export const TERRAK_PILLARS = [
  {
    title: "Ask the questions you actually have",
    body: "Combine datasets, define your own indices and thresholds. Over 150 datasets, yours to work with.",
  },
  {
    title: "Bring your own data",
    body: "Your cadastre, field records and proprietary layers, analysed alongside the archive.",
  },
  {
    title: "Answers in the format you need",
    body: "Map tiles, GeoTIFFs, JSON or spreadsheet summaries — whatever you expect.",
  },
  {
    title: "As deep or as brief as you need",
    body: "A single paddock over decades, or a continent reduced to one figure a board can decide on.",
  },
];

export const AER_STRIP = [
  { img: "/images/aus-env-report-2.webp", alt: "Continental vegetation monitoring" },
  { img: "/images/darling1.webp", alt: "Darling River catchment" },
  { img: "/images/globseas2.webp", alt: "Global vegetation seasonality composite" },
  { img: "/images/africa1.webp", alt: "Horn of Africa MODIS composite" },
];

export const AER_STATS = [
  {
    kicker: "Record",
    value: "25+",
    unit: "years",
    label: "Of satellite observations behind every figure",
  },
  {
    kicker: "Archive",
    value: "20+",
    unit: "TB",
    label: "Of spatial data, reconciled into one continental record",
  },
  {
    kicker: "Reach",
    value: "10",
    unit: "million",
    label: "Readers and counting",
  },
];

export const AER_STORY = [
  {
    step: "The question",
    body: "What is the state of Australia's land, natural resources and ecosystems this year, and what has changed?",
  },
  {
    step: "The challenge",
    body: "Reporting on a country's environment once meant booking time on a national supercomputer and running batch jobs — expensive, slow, rigid and hard to repeat.",
  },
  {
    step: "The data",
    body: "Decades of satellite observations and more than 20 TB of spatial data, reconciled into one continental record.",
  },
  {
    step: "The approach",
    body: "Working with Australia's Terrestrial Ecosystem Research Network (TERN) and the ANU, we built the data infrastructure behind the Report.",
  },
  {
    step: "The result",
    body: "Continental-scale analyses now run interactively and can be rerun or extended as new data arrives — no supercomputer booking required.",
  },
  {
    step: "The impact",
    body: "An accessible annual account of the nation's land and ecosystems for researchers, land managers and the public.",
  },
];

export const RESULTS = [
  {
    title: "Risk management",
    body: "Detect change early enough to act on it, rather than reporting it afterwards.",
  },
  {
    title: "Compliance",
    body: "Monitor continuously and demonstrate compliance, without putting anyone on the ground.",
  },
  {
    title: "Due diligence",
    body: "Support a valuation, a claim or an investment case with spatial evidence.",
  },
  {
    title: "Operations",
    body: "Replace manual analysis and automate reporting with something that runs on its own.",
  },
];

/** Carried over from v1 — the closing line v2 dropped from the results section. */
export const RESULTS_CODA =
  "When a question is easy to ask, the first answer is rarely the last. A finding on one property prompts the same look at forty others. The value turns up in places nobody planned for.";

export const RIGOUR = [
  {
    num: "01",
    title: "Research, made operational",
    body: "The methods behind our answers come from published, peer-reviewed science. We add the engineering that turns a paper into a continental-scale product.",
  },
  {
    num: "02",
    title: "Measured against reality",
    body: "Stream gauges, flux towers, field sensors, airborne LiDAR. Results are validated against physical measurements, not modelled estimates.",
  },
  {
    num: "03",
    title: "Uncertainty stated",
    body: "Every satellite and climate model carries uncertainty. Our outputs make that uncertainty transparent, so you know how far to trust a result.",
  },
  {
    num: "04",
    title: "Traceable to source",
    body: "Missions, dates and methods travel with every answer, so any figure can be traced back to the record behind it.",
  },
];

export const RIGOUR_CHAIN = [
  "Science",
  "Data",
  "Validation",
  "Traceability",
  "Defensible answer",
];

export const PEOPLE = [
  {
    name: "Dr Pablo Rozas Larraondo",
    initials: "PR",
    role: "Co-founder · AI and geospatial engineering",
    bio: "An artificial intelligence scientist specialising in large-scale geospatial systems. His work in AI, spatial data and large-scale computing shaped the technology behind Terrak.io and continues to guide its development.",
  },
  {
    name: "Professor Albert Van Dijk",
    initials: "AV",
    role: "Co-founder · Environmental science",
    bio: "Professor of Water Science and Management at the ANU's Fenner School of Environment and Society, where he established the Centre for Water and Landscape Dynamics. Two decades of continental-scale environmental research underpin much of the science behind Haizea.",
  },
];

export const PROCESS = [
  {
    num: "01",
    title: "Starting with your question",
    body: "What are you trying to understand, measure or decide? We work with you to turn the problem into a question the data can answer.",
  },
  {
    num: "02",
    title: "Testing what's possible",
    body: "One place, one question, one useful answer — see what the data can tell you before committing to something bigger.",
  },
  {
    num: "03",
    title: "Building it together",
    body: "Your domain expertise with our science and engineering, shaping an answer that works in the real world.",
  },
  {
    num: "04",
    title: "Making it operational in your business",
    body: "When the answer proves useful, we build your solution around it, and it grows with the next question — without rebuilding from the ground up.",
  },
];
